import { ComponentChild } from 'preact'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { ButtonToggle } from '@bonniernews/dn-design-system-web/components/button-toggle/button-toggle.tsx'

import { formatThousandsWithSpaces } from '../../helpers/formatThousandsWithSpaces.ts'

interface TagHeaderProps extends SharedProps {
  /** * H1 Title */
  title: string
  /** Heading of the link box */
  vignetteText?: string
  /** Target URL for the vignette */
  vignetteTargetUrl?: string
  /** Main image */
  media?: ComponentChild
  /** Intended use is text, links */
  descriptionHtml?: string
  /** Total articles on this list-page */
  articleCount?: number
  /** The theme-class to apply to the vignette */
  theme?: 'nyheter' | 'kultur'
  /** Show/hide toggle button */
  toggle?: boolean
  /** `text` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleText?: string
  /** `selectedText` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleSelectedText?: string
  /** `selected` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleSelected?: boolean
  /** `loading` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleLoading?: boolean
  /** `classNames` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleClassNames?: string
  /** `attributes` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleAttributes?: Record<string, unknown>
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/tag-header](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/tag-header)
 * - Storybook: [TagHeader](https://designsystem.dn.se/?path=/docs/section-tagheader--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/tag-header/tag-header.scss'`
 */
export const TagHeader = ({
  title,
  vignetteText,
  vignetteTargetUrl,
  media,
  descriptionHtml,
  articleCount,
  theme,
  toggle = false,
  toggleText,
  toggleSelectedText,
  toggleSelected = false,
  toggleLoading = false,
  toggleClassNames,
  toggleAttributes,
  attributes = {},
  classNames,
}: TagHeaderProps) => {
  const componentClassName = 'ds-tag-header'

  const classes = formatClassString([
    componentClassName,
    theme && `${componentClassName}--${theme}`,
    (!!descriptionHtml || !!articleCount) && `${componentClassName}--has-border`,
    classNames,
  ])

  return (
    <div className={classes} {...attributes}>
      <div className={`${componentClassName}__content`}>
        <div className={`${componentClassName}__bar`}>
          <div className={`${componentClassName}__heading`}>
            {vignetteText &&
              (vignetteTargetUrl ? (
                <a href={vignetteTargetUrl} className={`${componentClassName}__vignette`}>
                  {vignetteText}
                </a>
              ) : (
                <span className={`${componentClassName}__vignette`}>{vignetteText}</span>
              ))}
            <h1 className={`${componentClassName}__title`}>{title}</h1>
            {toggle && (
              <ButtonToggle
                variant='primary'
                size='md'
                isIconButton={false}
                text={toggleText}
                selectedText={toggleSelectedText || toggleText || ''}
                selected={toggleSelected}
                loading={toggleLoading}
                classNames={toggleClassNames}
                attributes={toggleAttributes as { [key: string]: string }}
              />
            )}
          </div>
          {media && <div className={`${componentClassName}__media`}>{media}</div>}
        </div>
        {descriptionHtml && (
          <div className={`${componentClassName}__description`} dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
        )}
        {/* Do not render element if articleCount=0 because A) we didn't in Bang B) poddPage may set it zero but doesn't wanna render it */}
        {articleCount !== undefined && articleCount > 0 && (
          <p className={`${componentClassName}__article-count`}>
            {formatThousandsWithSpaces(articleCount)} {articleCount === 1 ? 'artikel' : 'artiklar'}
          </p>
        )}
      </div>
    </div>
  )
}
